import React from 'react';
import { motion } from 'framer-motion';
import { Target, Users, Award, TrendingUp, Zap, Shield, Globe, Rocket } from 'lucide-react';
import PageTransition from '../components/PageTransition';

const About = () => {
  const values = [
    { icon: Target, title: 'Mission-Driven', desc: 'Building accessible financial tools for everyone, everywhere.' },
    { icon: Users, title: 'Community First', desc: 'Every decision is made with our community in mind.' },
    { icon: Shield, title: 'Security', desc: 'Military-grade security protecting your assets 24/7.' },
    { icon: Rocket, title: 'Innovation', desc: 'Constantly pushing boundaries in blockchain technology.' },
  ];

  const team = [
    { role: 'Lead Developer', desc: 'Full-stack blockchain development' },
    { role: 'Security Expert', desc: 'Smart contract auditing and security' },
    { role: 'Marketing Lead', desc: 'Community growth and partnerships' },
    { role: 'Product Manager', desc: 'User experience and product strategy' },
  ];

  return (
    <PageTransition>
      <div className="min-h-screen text-white bg-[#0A0A0A] pt-20">

        {/* Hero Section */}
        <section className="relative py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FF6B00]/5 via-transparent to-transparent" />

          <div className="relative max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              <img
                src="/dobix-logo.png"
                alt="Dobix Coin"
                className="h-24 w-24 mx-auto"
              />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-5xl md:text-6xl font-bold mb-6 font-heading"
            >
              About <span className="text-gradient">Dobix</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-xl text-gray-400 max-w-3xl mx-auto"
            >
              We're building the future of decentralized payments and messaging, making crypto accessible to everyone on the planet.
            </motion.p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl font-bold mb-6 font-heading">
                  Our Mission
                </h2>
                <p className="text-gray-300 text-lg mb-6">
                  Dobix Coin is on a mission to democratize access to cryptocurrency and make digital payments accessible to everyone, everywhere. We believe that financial freedom should be a fundamental right, not a privilege.
                </p>
                <p className="text-gray-300 text-lg mb-6">
                  By leveraging the power of Solana blockchain, we're creating a platform that combines the speed of modern payment systems with the security and transparency of blockchain technology.
                </p>
                <p className="text-gray-400">
                  Our vision is a world where anyone can send and receive money instantly, securely, and without borders.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="card-dark rounded-3xl p-8 text-center"
              >
                <img
                  src="/dobix-logo.png"
                  alt="Dobix"
                  className="h-40 w-40 mx-auto mb-6 float"
                />
                <p className="text-3xl font-bold text-gradient mb-2">50,000+</p>
                <p className="text-gray-400">Community Members</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 px-4 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FF6B00]/5 to-transparent" />
          <div className="relative max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-4 font-heading">
                Our Core Values
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-dark card-hover rounded-2xl p-6 text-center"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#FF6B00] to-[#FFB800] rounded-xl flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                  <p className="text-gray-400 text-sm">{value.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: '$10M+', label: 'Total Volume' },
                { value: '50K+', label: 'Holders' },
                { value: '150+', label: 'Countries' },
                { value: '99.9%', label: 'Uptime' },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="stat-card text-center"
                >
                  <p className="text-3xl font-bold text-gradient mb-1">{stat.value}</p>
                  <p className="text-gray-400 text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-4 font-heading">
                Built by <span className="text-gradient">Experts</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Our team combines decades of experience in blockchain, security, and fintech.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {team.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-dark rounded-2xl p-6 text-center"
                >
                  <div className="w-16 h-16 bg-[#222222] rounded-full mx-auto mb-4 flex items-center justify-center">
                    <span className="text-2xl">👤</span>
                  </div>
                  <h3 className="text-lg font-bold mb-1">{member.role}</h3>
                  <p className="text-gray-400 text-sm">{member.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default About;